import { UAParser } from 'ua-parser-js';

/**
 * Result of parsing a User-Agent string. All fields are non-empty strings —
 * unknown values fall back to sentinel labels rather than empty strings so
 * downstream UI never has to second-guess.
 */
export interface ParsedUserAgent {
    /** Browser name plus major version when available, e.g. "Chrome 121". */
    browser: string;
    /** Operating system name plus version when available, e.g. "macOS 14.5". */
    os: string;
    /** Combined human label, e.g. "Chrome 121 on macOS 14.5". */
    parsed: string;
    /** The original raw User-Agent string, preserved verbatim. */
    raw: string;
}

const UNKNOWN_BROWSER = 'Unknown browser';
const UNKNOWN_OS = 'Unknown OS';

/**
 * Parses a raw User-Agent header string into a small structured object. Used
 * by the sign-time provenance UI to surface a friendly "Chrome 121 on macOS"
 * label while still preserving the raw string for the auditor.
 *
 * The function never throws — empty / malformed inputs return sentinel
 * labels ("Unknown browser", "Unknown OS") rather than blanks.
 *
 * @param raw - Original User-Agent header. May be `undefined`-ish at the
 *              call site; callers should pass `''` for "absent".
 * @returns A {@link ParsedUserAgent} with `browser`, `os`, `parsed`, and `raw`.
 */
export function parseUserAgent(raw: string): ParsedUserAgent {
    const trimmed = raw?.trim() ?? '';
    if (trimmed.length === 0) {
        return {
            browser: UNKNOWN_BROWSER,
            os: UNKNOWN_OS,
            parsed: `${UNKNOWN_BROWSER} on ${UNKNOWN_OS}`,
            raw: raw ?? '',
        };
    }

    const result = new UAParser(raw).getResult();
    const browserName = result.browser.name;
    const browserVersion = result.browser.version;
    const osName = result.os.name;
    const osVersion = result.os.version;

    const browserLabel = browserName ? (browserVersion ? `${browserName} ${browserVersion.split('.')[0]}` : browserName) : UNKNOWN_BROWSER;
    const osLabel = osName ? (osVersion ? `${osName} ${osVersion}` : osName) : UNKNOWN_OS;

    return {
        browser: browserLabel,
        os: osLabel,
        parsed: `${browserLabel} on ${osLabel}`,
        raw,
    };
}
